import { makeAutoObservable, runInAction } from 'mobx';
import { EmfitAPI } from '../services/BaseAPI';
import { AUTH_API } from 'src/config';
import { SessionStore } from './SessionStore';

/**
 * Self-service two-factor configuration.
 *
 * The endpoint differs by principal: master users use /auth/user/2fa-configure,
 * group users /auth/group-user/2fa-configure. Both live under AUTH_API, not
 * STATUS_API.
 *
 * ConfigureTwoFactorDto validates phoneNumber with ValidateIf(enable === true),
 * so enabling without one is a 400. We block that client-side instead.
 */
class TwoFactorStoreClass {
  enabled = false;
  phoneNumber = '';
  loading = false;
  saving = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  private get endpoint(): string {
    return SessionStore.isMasterUser
      ? `${AUTH_API}/auth/user/2fa-configure`
      : `${AUTH_API}/auth/group-user/2fa-configure`;
  }

  async load(): Promise<void> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const result = await EmfitAPI.get<{
        twoFactorEnabled?: boolean;
        phoneNumber?: string;
      }>(this.endpoint, { noCache: true });
      runInAction(() => {
        this.enabled = !!result?.twoFactorEnabled;
        this.phoneNumber = result?.phoneNumber ?? '';
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to load two-factor settings';
        this.loading = false;
      });
      console.error('Failed to load 2FA configuration:', error);
    }
  }

  async save(enable: boolean, phoneNumber: string): Promise<boolean> {
    const trimmed = phoneNumber.trim();

    if (enable && !trimmed) {
      runInAction(() => {
        this.error =
          'A phone number is required to enable two-factor authentication';
      });
      return false;
    }

    runInAction(() => {
      this.saving = true;
      this.error = null;
    });

    try {
      await EmfitAPI.post(this.endpoint, {
        body: JSON.stringify({ enable, phoneNumber: trimmed }),
      });
      runInAction(() => {
        this.enabled = enable;
        this.phoneNumber = trimmed;
        this.saving = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to save two-factor settings';
        this.saving = false;
      });
      console.error('Failed to save 2FA configuration:', error);
      return false;
    }
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.enabled = false;
    this.phoneNumber = '';
    this.loading = false;
    this.saving = false;
    this.error = null;
  }
}

export const TwoFactorStore = new TwoFactorStoreClass();
